// Single source of truth for product pricing.
// All prices stored as integer EUR cents (e.g. 1900 = €19.00). EUR is the canonical currency.
// Display-layer conversions to other currencies happen at render time via `sku_prices` / `fx_rates`.

export const PRICING = {
  founder_pass: {
    monthly_eur_cents: 1900,
    annual_eur_cents: 17900,
  },
  bootcamp: {
    beginner_eur_cents:     9900,
    intermediate_eur_cents: 17900,
    advanced_eur_cents:     24900,
  },
  coaching: {
    tier_4h_eur_cents:  29900,
    tier_8h_eur_cents:  49900,
    tier_12h_eur_cents: 79900,
  },
  fractional:   { from_eur_cents: 149900 },
  microservice: { from_eur_cents:  49900 },
  team:         { seat_monthly_eur_cents: 9900, min_seats: 5 },
  // Sprint 3 — provider subscriptions and one-time add-ons
  provider_pro: {
    monthly_eur_cents: 2900,
    annual_eur_cents: 29000,
  },
  provider_elite: {
    monthly_eur_cents: 9900,
    annual_eur_cents: 99000,
  },
  addons: {
    verification_fasttrack_eur_cents: 4900,
    profile_spotlight_7d_eur_cents: 7900,
    extra_language_eur_cents: 1900,
  },
} as const;

export type PricingConfig = typeof PRICING;

export type PlanKind =
  | "founder_pass"
  | "bootcamp"
  | "coaching"
  | "fractional"
  | "microservice"
  | "team"
  | "provider_pro"
  | "provider_elite"
  | "addon";

export type PlanInterval = "one_time" | "monthly" | "annual" | "custom";

export interface PlanRow {
  id: string;
  kind: PlanKind;
  name: string;
  eur_cents: number;
  interval: PlanInterval;
  metadata: Record<string, unknown>;
}

/**
 * Flat plan rows derived from `PRICING`. Mirrors the rows seeded into the
 * Supabase `plans` table so client and server share identical IDs/amounts.
 */
export const PLAN_ROWS: PlanRow[] = [
  { id: "founder_pass.monthly", kind: "founder_pass", name: "Founder Pass — Monthly", eur_cents: PRICING.founder_pass.monthly_eur_cents, interval: "monthly", metadata: {} },
  { id: "founder_pass.annual",  kind: "founder_pass", name: "Founder Pass — Annual",  eur_cents: PRICING.founder_pass.annual_eur_cents,  interval: "annual",  metadata: {} },
  { id: "bootcamp.beginner",     kind: "bootcamp", name: "Bootcamp — Beginner",     eur_cents: PRICING.bootcamp.beginner_eur_cents,     interval: "one_time", metadata: { tier: "beginner" } },
  { id: "bootcamp.intermediate", kind: "bootcamp", name: "Bootcamp — Intermediate", eur_cents: PRICING.bootcamp.intermediate_eur_cents, interval: "one_time", metadata: { tier: "intermediate" } },
  { id: "bootcamp.advanced",     kind: "bootcamp", name: "Bootcamp — Advanced",     eur_cents: PRICING.bootcamp.advanced_eur_cents,     interval: "one_time", metadata: { tier: "advanced" } },
  { id: "coaching.4h",  kind: "coaching", name: "Coaching — 4h",  eur_cents: PRICING.coaching.tier_4h_eur_cents,  interval: "monthly", metadata: { hours: 4 } },
  { id: "coaching.8h",  kind: "coaching", name: "Coaching — 8h",  eur_cents: PRICING.coaching.tier_8h_eur_cents,  interval: "monthly", metadata: { hours: 8 } },
  { id: "coaching.12h", kind: "coaching", name: "Coaching — 12h", eur_cents: PRICING.coaching.tier_12h_eur_cents, interval: "monthly", metadata: { hours: 12 } },
  { id: "fractional.base",   kind: "fractional",   name: "Fractional Exec",      eur_cents: PRICING.fractional.from_eur_cents,   interval: "custom",  metadata: { from: true } },
  { id: "microservice.base", kind: "microservice", name: "Microservice",         eur_cents: PRICING.microservice.from_eur_cents, interval: "one_time", metadata: { from: true } },
  { id: "team.seat_monthly", kind: "team",         name: "Teams — Seat / month", eur_cents: PRICING.team.seat_monthly_eur_cents, interval: "monthly", metadata: { min_seats: PRICING.team.min_seats } },
  // Sprint 3 — expert subscriptions
  { id: "provider_pro.monthly",   kind: "provider_pro",   name: "Provider Pro — Monthly",   eur_cents: PRICING.provider_pro.monthly_eur_cents,   interval: "monthly", metadata: { tier: "pro" } },
  { id: "provider_pro.annual",    kind: "provider_pro",   name: "Provider Pro — Annual",    eur_cents: PRICING.provider_pro.annual_eur_cents,    interval: "annual",  metadata: { tier: "pro" } },
  { id: "provider_elite.monthly", kind: "provider_elite", name: "Provider Elite — Monthly", eur_cents: PRICING.provider_elite.monthly_eur_cents, interval: "monthly", metadata: { tier: "elite" } },
  { id: "provider_elite.annual",  kind: "provider_elite", name: "Provider Elite — Annual",  eur_cents: PRICING.provider_elite.annual_eur_cents,  interval: "annual",  metadata: { tier: "elite" } },
  // Sprint 3 — one-time add-ons
  { id: "addon.verification_fasttrack", kind: "addon", name: "Verification fast-track",   eur_cents: PRICING.addons.verification_fasttrack_eur_cents, interval: "one_time", metadata: { sku: "verification_fasttrack" } },
  { id: "addon.profile_spotlight_7d",   kind: "addon", name: "Profile spotlight (7 days)", eur_cents: PRICING.addons.profile_spotlight_7d_eur_cents,   interval: "one_time", metadata: { sku: "profile_spotlight_7d" } },
  { id: "addon.extra_language",         kind: "addon", name: "Extra language slot",        eur_cents: PRICING.addons.extra_language_eur_cents,         interval: "one_time", metadata: { sku: "extra_language" } },
];

export function eurMajor(cents: number): number {
  return cents / 100;
}

/**
 * Map a bootcamp `level` to the canonical EUR-cent tier price.
 * Beginner €99 / Intermediate €179 / Advanced €249.
 */
export function tierEurCentsFor(level?: string | null): number {
  switch ((level ?? "").toLowerCase()) {
    case "advanced":
      return PRICING.bootcamp.advanced_eur_cents;
    case "intermediate":
      return PRICING.bootcamp.intermediate_eur_cents;
    case "beginner":
    default:
      return PRICING.bootcamp.beginner_eur_cents;
  }
}